import { ReactNode } from 'react';
import { LogicalOperator, MatchingMethod } from '../../global';
import {
  AntennaSignalOptions,
  MonitoringMode,
  MovementStatus,
  TransportFuelType,
  TransportType,
} from '../../shared/types';
import { LocationLightDTO } from '../../services/location.service';

export type MonitoringTeam = {
  id: number;
  name: string;
  transportsCount: number;
};

export enum MonitoringReportOption {
  'SPEED' = 'speed',
  'TEMPERATURE' = 'temperature',
  'RELIEF' = 'altitude',
  'GSM_SIGNAL' = 'gsmSignal',
  'GPS_SIGNAL' = 'satellites',
  'VOLTAGE' = 'externalVoltage',
}

export interface MonitoringFlatTransportDTO {
  id: number;
  name: string | null;
  licensePlate: string | null;
  type: TransportType | null;
  imageId: string | null;
  status: MovementStatus;
  gpsSignalMetrics: AntennaSignalOptions;
  gsmSignalMetrics: AntennaSignalOptions;
  updates?: MonitoringUpdateDTO['data'];
  altitude?: number;
  bearing?: number;
  latitude?: number;
  longitude?: number;
  speed?: number;
  movement?: number;
  createdAt?: string;
  engineRpm?: number | null;
  satellites?: number;
  batteryVoltage?: number;
  externalVoltage?: number;
  gnssStatus?: number;
  gsmSignal?: number;
  courier?: MonitoringTransportDTO['courier'];
  team?: MonitoringTransportDTO['team'];
  device?: MonitoringTransportDTO['device'];
  maxSpeed?: number;
}

export type MonitoringSlice = {
  map: {
    teams: Record<string, MonitoringTeamDTO>;
    transports: Record<string, MonitoringFlatTransportDTO>;
    locations: {
      locationAll: Record<number, LocationLightDTO>;
      selectedLocation: LocationLightDTO | null;
      // filters: {};
    };
  };
  trackInfo: {
    allSelected: boolean;
    teamId: number | null;
    transportId: number | null;
  };
  sidebarFilters: {
    searchResults: {
      loading: boolean;
      data: MonitoringTransportDTO[];
      total: number;
      searching: boolean;
    };
    teamIds: number[];
  };
  general: MonitoringGeneralInfo;
  mode: MonitoringMode;
};

export const enum MonitoringSearchOption {
  LICENSE_PLATE = 'licensePlate',
  TRANSPORT_TYPE = 'vehicleType',
  DRIVER_NAME = 'name',
  TEAM_ID = 'team:id',
  TEAM_NAME = 'team:name',
}
export type MonitoringTransportSearchParams = {
  search?: string;
  team?: string[] | null;
};

export type MonitoringTransportDTO = {
  id: number;
  name: string | null;
  type: TransportType | null;
  licensePlate: string | null;
  vin: string | null;
  imageId: string | null;
  courier?: {
    id: number;
    firstName: string;
    lastName: string;
  };
  device?: {
    id: number;
    enabled: boolean;
    status: number;
    name: string;
    data?: {
      gsmSignal?: number;
      satellites?: number;
      speed: number;
      bearing: number;
      movement: number;
      ignition: number;
      createdAt: string;
      engineRpm: number | null;
      latitude: number;
      altitude: number;
      longitude: number;
      batteryVoltage: number;
      externalVoltage: number;
    };
  };
  team?: {
    id: number;
    name: string;
  };
  status: MovementStatus;
};

export interface PopOverItem {
  label: string;
  value: ReactNode;
  getIsActive?: () => boolean;
}

export interface MonitoringTeamDTO {
  id: number;
  name: string;
  total: number;
  transportIds: number[];
  areTransportsLoaded: boolean;
}

export interface MonitoringVehicleDetailedDTO {
  courierId: number;
  courierName: string;
  transportName: string;
  transportId: number;
  vehicleType: TransportType;
  licensePlate: string | null;
  currentKilometer: number;
  latitude: number;
  longitude: number;
  movement: number;
  speed: number;
  batteryVoltage: number;
  altitude: number;
  gsmSignal: number;
  gnssStatus: number;
  profileImageId: string | null;
  lastActive: string;
  transportStatus: MovementStatus;
  deviceStatus: 0 | 1;
  externalVoltage: number;
  satellites: number;
}

export interface RealTimeMessage {
  device: {
    id: number;
  };
  id: number;
  eventId: number;
  position: {
    lat: number;
    lng: number;
    alt: number;
  };
  bearing: number;
  satellites?: number;
  speed?: number;
  engineRpm: number | null;
  data: {
    totalOdometer: number | null;
    gsmSignal?: number;
    temperature: number | null;
    numberOfDtc: number | null;
    engineLoad: number | null;
    coolantTemperature: number | null;
    intakeMap: number | null;
    throttlePosition: number | null;
    fuelLevel: number | null;
    distanceSinceCodesClear: number | null;
    barometricPressure: number | null;
    ambientAirTemperature: null;
    externalVoltage: number | null;
    batteryVoltage: number | null;
    batteryCurrent: number | null;
    gnssStatus: number | null;
    dataMode: number | null;
    batteryLevel: number | null;
    tripOdometer: number | null;
    ignition: number | null;
    overSpeeding: number | null;
    greenDrivingDuration: number | null;
    vin: number | null;
  };
  greenDrivingValue: number | null;
  createdAt: string;
}

export interface MonitoringUpdateDTO {
  transportId: number;
  movementStatus: MovementStatus;
  data: RealTimeMessage[];
}

export type MonitoringRealTimeUpdate = {
  transportId: number;
  movementStatus: MovementStatus;
  data: RealTimeMessage;
};

export interface MonitoringRequestFilter {
  startDate: string;
  endDate?: string;
  transportId: number;
  limit?: number;
  source?: 'monitoring' | 'alerts';
  exactTripID?: string;
  includeMessages: boolean;
}

export type MonitoringTripRequestFilter = MonitoringRequestFilter;

export interface MonitoringGeneralRequestFilter {
  startDate: string;
  endDate?: string;
  transportId: number;
}
export { MatchingMethod, LogicalOperator };

export interface MonitoringGeneralInfo {
  avgConsumption: number | null;
  avgSpeed: number | null;
  distancePassed: number | null;
  drivingTime: number | null;
  fuelConsumption: number | null;
  humidity: string | null;
  idlingTime: number | null;
  parkingTime: number | null;
  temperature: string | null;
  fuelType: TransportFuelType;
  isLoading: boolean;
  maxSpeed: null | number;
}

export type MonitoringDeviceHistoryTab = 'general' | 'notifications' | 'trips';
export type MonitoringDateFilter =
  | 'custom'
  | 'today'
  | 'yesterday'
  | 'lastWeek'
  | 'lastMonth';
